import SiteCoreLinks from "@/components/SiteCoreLinks";
// /en/translate/naati/online — NAATI Online Hub (EN)
import { createFileRoute, Link } from "@tanstack/react-router";
import { NAATI_COUNTRIES } from "@/data/naati-countries";
import { naatiOnlineHubSectionsEn } from "@/data/naati-online-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NaatiAuthorByline from "@/components/NaatiAuthorByline";
import NaatiDoctypeMatrix from "@/components/NaatiDoctypeMatrix";
import { naatiOfferCatalog, naatiServiceSchema, naatiSpeakableSchema, naatiHowToSchema, naatiPersonGraph } from "@/lib/naati-schema";
import { Globe2, MessageCircle, Phone, Plane, MapPin } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/translate/naati/online/")({
  head: () => {
    const url = `${SITE}/en/translate/naati/online`;
    const title = "NAATI Online Worldwide — Certified Translation Shipped to 50+ Countries | NYC";
    const desc = "100% online NAATI-certified translation with DHL Express shipping to 50+ countries. Get your timeline and checklist in minutes — chat now.";
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/online", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/online", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/online", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/online", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/translate/naati/online", "/en/translate/naati/online")],
      scripts: [
        naatiSpeakableSchema(url),
        naatiOfferCatalog({ url, inLanguage: "en" }),
        naatiServiceSchema({ url, name: title, description: desc, inLanguage: "en" }),
        naatiHowToSchema({
          url,
          name: "How to order NAATI Online translation shipped worldwide",
          inLanguage: "en",
          steps: [
            { name: "Send scans via LINE", text: "Send a 300dpi+ scan of each page via LINE @nycli with destination country." },
            { name: "Confirm quote + shipping", text: "We email a fixed quote within 30 minutes including DHL Express to your country." },
            { name: "Translate + NAATI stamp", text: "A NAATI Certified Translator (Thai↔English) applies stamp and CPN in 2-4 business days." },
            { name: "DHL Express worldwide", text: "Hard copies ship in 3-6 days with tracking; PDFs are emailed the same day translation completes." },
          ],
          estimatedCostThb: 800,
        }),
        naatiPersonGraph(),
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "NAATI Online Worldwide Translation",
              serviceType: "NAATI Certified Translation with Global Shipping",
              areaServed: { "@type": "Place", name: "Worldwide" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: 800, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
        ]),
      ],
    };
  },
  component: NaatiOnlineHubEnPage,
});

function NaatiOnlineHubEnPage() {
  const sections = naatiOnlineHubSectionsEn();
  const byRegion = NAATI_COUNTRIES.reduce<Record<string, typeof NAATI_COUNTRIES>>((acc, c) => {
    (acc[c.region] ||= [] as typeof NAATI_COUNTRIES).push(c);
    return acc;
  }, {});
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/naati" className="hover:text-gold">NAATI</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">Online Worldwide</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> NAATI Online · Worldwide
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            NAATI Online Worldwide — Certified Translation Shipped Globally
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg" data-speakable>
            100% online ordering. NAATI Professional Translators. DHL Express to 50+ countries. from THB 1,300 per page, 2 to 5 day turnaround.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <PoliceSections sections={sections} />

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="mr-2 inline-block h-6 w-6 text-amber-600" />
            Destination Countries (50+)
          </h2>
          {Object.entries(byRegion).map(([region, countries]) => (
            <div key={region} className="mt-6">
              <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-amber-700">{region}</h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {countries.map((c) => (
                  <Link
                    key={c.slug}
                    to="/en/translate/naati/online/$country"
                    params={{ country: c.slug }}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50"
                  >
                    <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                    <span className="font-semibold text-slate-800">{c.nameEn}</span>
                    <span className="ml-auto text-[10px] text-slate-500">{c.shippingDays}</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>

        <div className="mt-14"><ContactCTA /></div>
      </div>

      <NaatiDoctypeMatrix lang="en" />
      <NaatiAuthorByline lang="en" />

      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    </main>
  );
}
